import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { inventoryAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search, Printer, FileSpreadsheet } from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, LabelList, ReferenceLine, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { RejectionItemDrilldown } from '@/components/RejectionItemDrilldown';

const COMPANY_LOGO = '/company_logo.png';
const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const iso = (d) => d.toISOString().slice(0, 10);
const TYPE_LABEL = { closure_scrap: 'Scrap at closure', mrb_reject: 'MRB reject', rework: 'Rework' };
const TYPE_CLS = {
  closure_scrap: 'bg-slate-100 text-slate-700', mrb_reject: 'bg-red-100 text-red-700', rework: 'bg-orange-100 text-orange-700',
};

function Kpi({ label, value, testid, tone = 'text-foreground' }) {
  return (
    <Card>
      <CardContent className="p-3">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className={`text-lg font-semibold font-mono ${tone}`} data-testid={testid}>{value}</p>
      </CardContent>
    </Card>
  );
}

const TargetLine = ({ target }) => (
  <ReferenceLine yAxisId="pct" y={target} stroke="#111827" strokeDasharray="6 4" ifOverflow="extendDomain"
    label={{ value: `Target ${target}%`, position: 'insideTopLeft', fontSize: 10, fill: '#111827' }} />
);

const AboveBadge = () => (
  <Badge className="ml-1 text-[9px] bg-red-600 text-white hover:bg-red-600" data-testid="above-target-badge">Above target</Badge>
);

function TrendChart({ monthly, target }) {
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm">Monthly rejection % and PPM</CardTitle></CardHeader>
      <CardContent className="h-72" data-testid="rejection-trend-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={monthly}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="pct" tick={{ fontSize: 11 }} unit="%" />
            <YAxis yAxisId="ppm" orientation="right" tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar yAxisId="ppm" dataKey="ppm" name="PPM" fill="#f87171" />
            <Line yAxisId="pct" dataKey="rejection_pct" name="Rejection %" stroke="#b91c1c" strokeWidth={2} dot />
            <TargetLine target={target} />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function ReasonTable({ rows }) {
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm">By reason</CardTitle></CardHeader>
      <CardContent className="space-y-2" data-testid="rejection-by-reason">
        {rows.length === 0 ? <p className="text-xs text-muted-foreground">No rejections in this period</p> : rows.map(r => (
          <div key={r.reason} className="text-xs" data-testid={`reason-row-${r.reason}`}>
            <div className="flex justify-between"><span>{r.reason}</span><span className="font-mono">{fmt(r.quantity)} · {r.share_pct}%</span></div>
            <div className="h-1.5 bg-muted rounded mt-1"><div className="h-1.5 bg-red-500 rounded" style={{ width: `${r.share_pct}%` }} /></div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

const pctTxt = (v) => (v === null || v === undefined ? 'n/a' : `${v}%`);
const ppmTxt = (v) => (v === null || v === undefined ? 'n/a' : fmt(v));

function BreakdownTable({ title, rows, showCode, testid, onRowClick }) {
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm">{title} ({rows.length})</CardTitle></CardHeader>
      <CardContent className="p-0 overflow-x-auto max-h-96 overflow-y-auto">
        <table className="w-full text-xs" data-testid={testid}>
          <thead className="sticky top-0 bg-muted"><tr className="border-b text-muted-foreground">
            {[showCode ? 'Item' : 'Customer', 'Produced', 'Rejected', 'Reworked', 'Rej %', 'PPM', 'Share'].map(h =>
              <th key={h} className="text-left p-2 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={7} className="p-3 text-muted-foreground">No rejections in this period</td></tr>
              : rows.map(r => (
                <tr key={r.key} data-testid={`${testid}-row`}
                  className={`border-b last:border-0 ${r.above_target ? 'bg-red-50' : ''} ${onRowClick ? 'cursor-pointer hover:bg-muted/60' : ''}`}
                  onClick={onRowClick ? () => onRowClick(r) : undefined}>
                  <td className="p-2">{showCode && <span className={`font-mono mr-1 ${onRowClick ? 'text-blue-700 underline' : ''}`}>{r.code}</span>}
                    <span className={showCode ? 'text-muted-foreground' : ''}>{r.name}</span>
                    {r.above_target && <AboveBadge />}</td>
                  <td className="p-2 font-mono">{fmt(r.produced)}</td>
                  <td className="p-2 font-mono text-red-700">{fmt(r.rejected)}</td>
                  <td className="p-2 font-mono text-orange-700">{fmt(r.reworked)}</td>
                  <td className="p-2 font-mono">{pctTxt(r.rejection_pct)}</td>
                  <td className="p-2 font-mono">{ppmTxt(r.ppm)}</td>
                  <td className="p-2 font-mono">{r.share_pct}%</td>
                </tr>
              ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function PrintBreakdown({ title, rows, showCode }) {
  const top = rows.slice(0, 15);
  return (
    <div className="flex-1">
      <div className="text-xs font-semibold mb-1">{title}{rows.length > 15 ? ` (top 15 of ${rows.length})` : ''}</div>
      <table className="w-full text-[10px] border-collapse">
        <thead><tr>{[showCode ? 'Item' : 'Customer', 'Produced', 'Rejected', 'Rework', 'Rej %', 'PPM', 'Share', 'Vs target'].map(h =>
          <th key={h} className="border border-black p-0.5 text-left">{h}</th>)}</tr></thead>
        <tbody>
          {top.length === 0 ? <tr><td colSpan={8} className="border border-black p-1">No rejections</td></tr>
            : top.map(r => (
              <tr key={r.key}>{[showCode ? `${r.code} ${r.name}` : r.name, fmt(r.produced), fmt(r.rejected), fmt(r.reworked),
                pctTxt(r.rejection_pct), ppmTxt(r.ppm), `${r.share_pct}%`].map((v, i) =>
                <td key={i} className="border border-black p-0.5">{v}</td>)}
                <td className={`border border-black p-0.5 font-bold ${r.above_target ? 'text-red-700' : ''}`}>{r.above_target ? 'ABOVE' : 'OK'}</td></tr>))}
        </tbody>
      </table>
    </div>
  );
}

function RecordsTable({ records }) {
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm">Detail ({records.length})</CardTitle></CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-xs" data-testid="rejection-records-table">
          <thead><tr className="border-b bg-muted/50 text-muted-foreground">
            {['Date', 'Type', 'Item', 'Route card', 'Qty', 'Reason', 'Rework WO', 'By'].map(h =>
              <th key={h} className="text-left p-2 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {records.map(r => (
              <tr key={r.id} className="border-b last:border-0">
                <td className="p-2 font-mono">{r.date}</td>
                <td className="p-2"><Badge className={`text-[10px] ${TYPE_CLS[r.type]}`}>{TYPE_LABEL[r.type]}</Badge></td>
                <td className="p-2">{r.item_unique_id} <span className="text-muted-foreground">{r.item_name}</span></td>
                <td className="p-2 font-mono">{r.route_card_number || '-'}</td>
                <td className="p-2 font-mono">{fmt(r.quantity)}</td>
                <td className="p-2">{r.reason}</td>
                <td className="p-2 font-mono">{r.rework_wo_number || '-'}</td>
                <td className="p-2 text-muted-foreground">{r.created_by_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function PrintSheet({ data }) {
  const s = data.summary;
  const kpis = [['Produced', fmt(s.produced)], ['Rejected', fmt(s.rejected)], ['Scrap at closure', fmt(s.scrapped_at_closure)],
    ['MRB rejects', fmt(s.mrb_rejected)], ['Sent to rework', fmt(s.reworked)],
    ['Rejection %', `${s.rejection_pct}% (target ${data.target_pct}%)`], ['PPM', fmt(s.ppm)]];
  return createPortal(
    <div id="rejection-print" className="rej-print-only" data-testid="rejection-print-sheet">
      <div className="flex justify-between items-end border-b-2 border-black pb-1 mb-3">
        <div className="flex items-center gap-3">
          <img src={COMPANY_LOGO} alt="BHPL" className="h-14 w-auto" data-testid="print-logo" />
          <div>
            <div className="text-sm font-bold">BHAGYASHRI HOME APPLIANCES PVT LTD</div>
            <div className="text-[10px]">N-93, Hingna MIDC, Hingna Road, Nagpur - 440016</div>
            <div className="text-xl font-bold mt-1">Rejection Report</div>
            <div className="text-xs">Period: {data.from_date} to {data.to_date}</div>
          </div>
        </div>
        <div className="text-[10px] text-right">Rejected = scrap at closure + MRB rejects<br />% and PPM against total produced
          <br />Printed {new Date().toLocaleString('en-IN')}</div>
      </div>
      <div className="grid grid-cols-7 gap-2 mb-3">
        {kpis.map(([l, v]) => (
          <div key={l} className="border border-black p-1.5 text-center">
            <div className="text-[9px] uppercase">{l}</div><div className="text-lg font-bold">{v}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-4">
        <div>
          <div className="text-xs font-semibold mb-1">Monthly rejection % and PPM</div>
          <ComposedChart width={720} height={330} data={data.monthly} margin={{ top: 18, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
            <XAxis dataKey="month" tick={{ fontSize: 10 }} />
            <YAxis yAxisId="pct" tick={{ fontSize: 10 }} unit="%" />
            <YAxis yAxisId="ppm" orientation="right" tick={{ fontSize: 10 }} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar yAxisId="ppm" dataKey="ppm" name="PPM" fill="#f87171" isAnimationActive={false}>
              <LabelList dataKey="ppm" position="insideBottom" fontSize={9} fill="#111" />
            </Bar>
            <Line yAxisId="pct" dataKey="rejection_pct" name="Rejection %" stroke="#b91c1c" strokeWidth={2} isAnimationActive={false} />
            <TargetLine target={data.target_pct} />
          </ComposedChart>
        </div>
        <div className="flex-1">
          <div className="text-xs font-semibold mb-1">By reason</div>
          <table className="w-full text-[11px] border-collapse">
            <thead><tr>{['Reason', 'Qty', 'Share'].map(h => <th key={h} className="border border-black p-1 text-left">{h}</th>)}</tr></thead>
            <tbody>
              {data.by_reason.length === 0
                ? <tr><td colSpan={3} className="border border-black p-1">No rejections</td></tr>
                : data.by_reason.map(r => (
                  <tr key={r.reason}><td className="border border-black p-1">{r.reason}</td>
                    <td className="border border-black p-1">{fmt(r.quantity)}</td>
                    <td className="border border-black p-1">{r.share_pct}%</td></tr>))}
            </tbody>
          </table>
          <div className="text-xs font-semibold mt-3 mb-1">Monthly figures</div>
          <table className="w-full text-[10px] border-collapse">
            <thead><tr>{['Month', 'Produced', 'Rejected', '%', 'PPM'].map(h => <th key={h} className="border border-black p-0.5 text-left">{h}</th>)}</tr></thead>
            <tbody>{data.monthly.map(m => (
              <tr key={m.month}>{[m.month, fmt(m.produced), fmt(m.rejected), `${m.rejection_pct}%`, fmt(m.ppm)].map((v, i) =>
                <td key={i} className="border border-black p-0.5">{v}</td>)}</tr>))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex gap-4 mt-4" style={{ breakBefore: 'page', pageBreakBefore: 'always' }} data-testid="print-breakdowns">
        <PrintBreakdown title="By item" rows={data.by_item || []} showCode />
        <PrintBreakdown title="By customer" rows={data.by_customer || []} />
      </div>
    </div>,
    document.body,
  );
}

export default function RejectionReportPage() {
  const today = new Date();
  const [from, setFrom] = useState(iso(new Date(today.getFullYear(), today.getMonth() - 5, 1)));
  const [to, setTo] = useState(iso(today));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drillItem, setDrillItem] = useState(null);
  const [targetInput, setTargetInput] = useState('');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await inventoryAPI.rejectionReport({ from_date: from, to_date: to });
      setData(res.data);
      setTargetInput(String(res.data.target_pct));
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not load the rejection report'); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveTarget = async () => {
    try {
      await inventoryAPI.setRejectionTarget(parseFloat(targetInput));
      toast.success('Target saved');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not save the target'); }
  };

  const exportExcel = async () => {
    try {
      const res = await inventoryAPI.exportRejectionReport({ from_date: from, to_date: to });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `rejection_report_${from}_${to}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Could not export the report'); }
  };

  const s = data?.summary || {};
  return (
    <div className="space-y-4" data-testid="rejection-report-page">
      <div>
        <h1 className="text-xl font-semibold">Rejection Report</h1>
        <p className="text-xs text-muted-foreground">
          Rejected = scrap at route card closure + MRB rejects. Rejection % and PPM are against total produced in the period.
        </p>
      </div>
      <Card>
        <CardContent className="p-3 flex items-end gap-3 flex-wrap">
          <div><Label className="text-xs">From</Label>
            <Input type="date" value={from} onChange={e => setFrom(e.target.value)} className="h-8 text-xs" data-testid="rej-from" /></div>
          <div><Label className="text-xs">To</Label>
            <Input type="date" value={to} onChange={e => setTo(e.target.value)} className="h-8 text-xs" data-testid="rej-to" /></div>
          <Button size="sm" onClick={load} disabled={loading} data-testid="rej-apply">
            {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1" />} Show
          </Button>
          {data && (
            <div className="flex items-end gap-1.5" data-testid="rej-target-box">
              <div><Label className="text-xs">Target rejection %</Label>
                <Input type="number" step="0.01" min="0" value={targetInput} disabled={!isAdmin}
                  onChange={e => setTargetInput(e.target.value)} className="h-8 w-24 text-xs" data-testid="rej-target-input" /></div>
              {isAdmin && (
                <Button size="sm" variant="outline" onClick={saveTarget} data-testid="rej-target-save"
                  disabled={!(parseFloat(targetInput) > 0) || parseFloat(targetInput) === data.target_pct}>Save</Button>
              )}
            </div>
          )}
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" onClick={() => window.print()} disabled={!data} data-testid="rej-print">
              <Printer className="w-3.5 h-3.5 mr-1" /> Print / PDF
            </Button>
            <Button size="sm" variant="outline" onClick={exportExcel} disabled={!data} data-testid="rej-excel">
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Excel
            </Button>
          </div>
        </CardContent>
      </Card>
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <Kpi label="Produced" value={fmt(s.produced)} testid="kpi-produced" />
            <Kpi label="Rejected" value={fmt(s.rejected)} testid="kpi-rejected" tone="text-red-700" />
            <Kpi label="Scrap at closure" value={fmt(s.scrapped_at_closure)} testid="kpi-scrap" />
            <Kpi label="MRB rejects" value={fmt(s.mrb_rejected)} testid="kpi-mrb-reject" />
            <Kpi label="Sent to rework" value={fmt(s.reworked)} testid="kpi-reworked" tone="text-orange-700" />
            <Kpi label={`Rejection % (target ${data.target_pct}%)`} value={`${s.rejection_pct}%`} testid="kpi-pct"
              tone={s.rejection_pct > data.target_pct ? 'text-red-700' : 'text-emerald-700'} />
            <Kpi label="PPM" value={fmt(s.ppm)} testid="kpi-ppm" tone="text-red-700" />
          </div>
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2"><TrendChart monthly={data.monthly} target={data.target_pct} /></div>
            <ReasonTable rows={data.by_reason} />
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            <BreakdownTable title="By item — click an item for details" rows={data.by_item || []} showCode testid="rejection-by-item"
              onRowClick={(r) => setDrillItem(r.key)} />
            <BreakdownTable title="By customer" rows={data.by_customer || []} testid="rejection-by-customer" />
          </div>
          <RecordsTable records={data.records} />
          <PrintSheet data={data} />
          <RejectionItemDrilldown itemId={drillItem} from={data.from_date} to={data.to_date} onClose={() => setDrillItem(null)} />
        </>
      )}
    </div>
  );
}
